import React, {useState} from 'react';
import {Box, Form, Notification} from 'react-bulma-components';
import * as jose from 'jose';
import {isEmptyString} from '../utils/string.utils';
import DecodedJWS from '../components/DecodedJWS';

interface SuccessfulValidationResult {
  header: {[key: string]: any};
  payload: {[key: string]: any};
}

interface FailedValidationResult {
  message: string;
}

interface ValidationResult {
  isValid: boolean;
  data: SuccessfulValidationResult | FailedValidationResult;
}

const renderSuccessfulValidationResult = (
  {header, payload}: SuccessfulValidationResult
): React.ReactElement => (
  <>
    <p className="is-size-4">
      Decoded
    </p>

    <DecodedJWS
      header={header}
      payload={payload}
    />
  </>
);

const JWSValidationPage: React.FC = () => {
  const [token, setToken] = useState<string>('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const onTokenChanged = async (newToken: string): Promise<void> => {
    setToken(newToken);
    setValidationResult(null);

    if (isEmptyString(newToken)) {
      return;
    }

    try {
      const {payload, protectedHeader} = await jose.jwtVerify(newToken, jose.EmbeddedJWK);

      setValidationResult({
        isValid: true,
        data: {
          payload,
          header: protectedHeader
        }
      });
    } catch (e: any) {
      setValidationResult({
        isValid: false,
        data: {
          message: e?.message ?? 'JWS validation is failed'
        }
      });
    }
  };

  const isValidationFailed = validationResult?.isValid === false;

  return (
    <Box>
      <p className="is-size-4">
        Encoded JWS
      </p>

      <Form.Field>
        <Form.Control>
          <Form.Textarea
            color={isValidationFailed ? 'danger' : 'success'}
            value={token}
            onChange={({target}) => onTokenChanged(target.value)}
            rows={7}
          />
        </Form.Control>
      </Form.Field>

      {validationResult &&
        <>
          {validationResult.isValid ?
            renderSuccessfulValidationResult(validationResult.data as SuccessfulValidationResult) :
            <>
              <Notification color="danger">
                {(validationResult.data as FailedValidationResult).message}
              </Notification>
            </>
          }
        </>
      }
    </Box>
  );
};

export default JWSValidationPage;
